<template>
  <div>
    <!-- MAIN CONTENT -->
    <div class="content-body">
      <b-overlay
        :show="loadingOverlay"
        rounded="sm"
        opacity="0.85"
        variant="dark"
        no-spinner
      >
        <template #overlay>
          <div class="text-center">
            <div class="mb-3">
              <b-spinner
                variant="light"
                style="width: 3rem; height: 3rem;"
              />
            </div>
            <h5 class="text-white font-weight-bold mb-2">
              Enviando Operação
            </h5>
            <p class="text-white-50 mb-0">
              Processando seu sinal de compra...
            </p>
          </div>
        </template>
        <b-row class="match-height">
          <!-- FORMULÁRIO DE ENTRADA -->
          <b-col
            cols="12"
            lg="6"
            xl="5"
            class="order-lg-1 order-1"
          >
            <b-card
              no-body
              class="card-transaction"
            >
              <b-card-header class="d-flex justify-content-between align-items-center">
                <h4 class="card-title mb-0">
                  <feather-icon
                    icon="PlusCircleIcon"
                    class="mr-1"
                  />
                  Dados da Operação
                </h4>
                <b-badge
                  variant="light-primary"
                  pill
                >
                  Spot Trading
                </b-badge>
              </b-card-header>
              <b-card-body class="p-3">
                <validation-observer ref="formSignal">
                  <b-form @submit.prevent="submitForm">
                    <!-- CORRETORA SELECTION -->
                    <div class="form-section mb-2">
                      <h6 class="section-title">
                        <feather-icon
                          icon="ServerIcon"
                          size="16"
                          class="mr-1"
                        />
                        Corretora
                      </h6>
                      <b-row>
                        <b-col cols="12">
                          <div class="exchange-selector-form">
                            <div class="exchange-options">
                              <div
                                v-for="(exchange, key) in fixedExchanges"
                                :key="key"
                                class="exchange-option"
                                :class="{ 'active': selectedExchange === key }"
                                @click="selectExchange(key)"
                              >
                                <div class="option-content">
                                  <img
                                    :src="require(`@/assets/images/logo/${key}-logo.png`)"
                                    :alt="exchange.name"
                                    class="exchange-logo"
                                  >
                                  <div class="exchange-info">
                                    <span class="exchange-name">{{ exchange.name }}</span>
                                  </div>
                                </div>
                                <div
                                  v-if="selectedExchange === key"
                                  class="selected-indicator"
                                >
                                  <feather-icon
                                    icon="CheckCircleIcon"
                                    size="20"
                                    class="text-success"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </b-col>
                      </b-row>
                    </div>

                    <!-- PERFIL SELECTION -->
                    <div
                      v-if="deveMostrarPerfil"
                      class="form-section mb-2"
                    >
                      <h6 class="section-title">
                        <feather-icon
                          icon="UserIcon"
                          size="16"
                          class="mr-1"
                        />
                        Perfil de Trading
                      </h6>
                      <validation-provider
                        #default="{ errors }"
                        name="perfil"
                        rules="required"
                      >
                        <b-form-select
                          v-model="sinal.agentePerfilTradeId"
                          :options="perfisFiltrados"
                          text-field="nome"
                          value-field="id"
                          class="form-control-lg"
                        >
                          <template #first>
                            <b-form-select-option
                              :value="null"
                              disabled
                            >
                              Selecione um perfil
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </div>

                    <!-- PAR DE TRADING -->
                    <div class="form-section mb-2">
                      <h6 class="section-title">
                        <feather-icon
                          icon="DollarSignIcon"
                          size="16"
                          class="mr-1"
                        />
                        Par de Trading
                      </h6>
                      <b-row>
                        <b-col
                          cols="12"
                          md="6"
                        >
                          <b-form-group
                            label="Moeda Base"
                            label-for="moeda"
                          >
                            <validation-provider
                              #default="{ errors }"
                              name="Moeda"
                              :rules="{ required: true }"
                            >
                              <b-input-group>
                                <b-input-group-prepend is-text>
                                  <feather-icon
                                    icon="CoinsIcon"
                                    size="16"
                                  />
                                </b-input-group-prepend>
                                <b-form-input
                                  id="moeda"
                                  v-model="sinal.moedaTrade"
                                  placeholder="Ex: BTC, ETH, ADA"
                                  class="text-uppercase"
                                  @input="sinal.moedaTrade = sinal.moedaTrade.toUpperCase()"
                                />
                              </b-input-group>
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                        <b-col
                          cols="12"
                          md="6"
                        >
                          <b-form-group
                            label="Paridade"
                            label-for="paridade"
                          >
                            <validation-provider
                              #default="{ errors }"
                              name="Moeda paridade"
                              :rules="{ required: true }"
                            >
                              <b-form-select
                                id="paridade"
                                v-model="paridadeEscolhida"
                                :options="paridadeOptions"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </div>

                    <!-- CONFIGURAÇÕES DE ORDEM -->
                    <div class="form-section mb-2">
                      <h6 class="section-title">
                        <feather-icon
                          icon="SettingsIcon"
                          size="16"
                          class="mr-1"
                        />
                        Configurações da Ordem
                      </h6>
                      <b-row>
                        <b-col
                          cols="12"
                          md="6"
                        >
                          <b-form-group
                            label="Tipo de Ordem"
                            label-for="tipoOrdem"
                          >
                            <validation-provider
                              #default="{ errors }"
                              name="tipoOrdem"
                              :rules="{ required: true }"
                            >
                              <b-form-select
                                id="tipoOrdem"
                                v-model="sinal.ordemTipo"
                                :options="tipoOrdensOptions"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                        <b-col
                          v-if="sinal.ordemTipo !== 'market'"
                          cols="12"
                          md="6"
                        >
                          <b-form-group
                            label="Preço Limite"
                            label-for="limite"
                          >
                            <validation-provider
                              #default="{ errors }"
                              name="Limite"
                              :rules="{ required: sinal.ordemTipo === 'limit' || sinal.ordemTipo === 'stop_limit', regex: /^[+]?\d+(\.\d+)?$/ }"
                            >
                              <b-input-group>
                                <b-input-group-prepend is-text>
                                  <feather-icon
                                    icon="DollarSignIcon"
                                    size="16"
                                  />
                                </b-input-group-prepend>
                                <b-form-input
                                  id="limite"
                                  v-model="sinal.valorCompra"
                                  :disabled="sinal.ordemTipo === 'market'"
                                  placeholder="0.00000000"
                                  type="number"
                                  step="any"
                                />
                              </b-input-group>
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                      </b-row>

                      <!-- STOP LIMIT FIELD -->
                      <b-row v-if="sinal.ordemTipo === 'stop_limit'">
                        <b-col
                          cols="12"
                          md="6"
                        >
                          <b-form-group
                            label="Preço Stop"
                            label-for="valorCompraStop"
                          >
                            <validation-provider
                              #default="{ errors }"
                              name="Stop limite"
                              :rules="{ required: sinal.ordemTipo === 'stop_limit', regex: /^[+]?\d+(\.\d+)?$/ }"
                            >
                              <b-input-group>
                                <b-input-group-prepend is-text>
                                  <feather-icon
                                    icon="ShieldIcon"
                                    size="16"
                                  />
                                </b-input-group-prepend>
                                <b-form-input
                                  id="valorCompraStop"
                                  v-model="sinal.valorCompraStop"
                                  placeholder="0.00000000"
                                  type="number"
                                  step="any"
                                />
                              </b-input-group>
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </div>

                    <!-- ALVOS E STOP LOSS -->
                    <div class="form-section mb-2">
                      <h6 class="section-title">
                        <feather-icon
                          icon="TargetIcon"
                          size="16"
                          class="mr-1"
                        />
                        Alvos e Stop Loss
                      </h6>
                      <b-row>
                        <b-col
                          cols="12"
                          md="6"
                        >
                          <b-form-group
                            label="Alvo 1"
                            label-for="alvo1"
                          >
                            <validation-provider
                              #default="{ errors }"
                              name="Alvo 1"
                              :rules="{ required: true, regex: /^[+]?\d+(\.\d+)?$/ }"
                            >
                              <b-input-group>
                                <b-input-group-prepend is-text>
                                  <feather-icon
                                    icon="TrendingUpIcon"
                                    size="16"
                                    class="text-success"
                                  />
                                </b-input-group-prepend>
                                <b-form-input
                                  id="alvo1"
                                  v-model="sinal.alvo1"
                                  placeholder="0.00000000"
                                  type="number"
                                  step="any"
                                />
                              </b-input-group>
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                        <b-col
                          cols="12"
                          md="6"
                        >
                          <b-form-group
                            label="Alvo 2 (Opcional)"
                            label-for="alvo2"
                          >
                            <validation-provider
                              #default="{ errors }"
                              name="Alvo 2"
                              :rules="{ required: false, regex: /^[+]?\d+(\.\d+)?$/ }"
                            >
                              <b-input-group>
                                <b-input-group-prepend is-text>
                                  <feather-icon
                                    icon="TrendingUpIcon"
                                    size="16"
                                    class="text-success"
                                  />
                                </b-input-group-prepend>
                                <b-form-input
                                  id="alvo2"
                                  v-model="sinal.alvo2"
                                  placeholder="0.00000000"
                                  type="number"
                                  step="any"
                                />
                              </b-input-group>
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                      </b-row>

                      <!-- STOP LOSS -->
                      <b-row>
                        <b-col
                          cols="12"
                          md="6"
                        >
                          <b-form-group label="Stop Loss">
                            <div class="d-flex align-items-center mb-1">
                              <b-form-checkbox
                                v-model="stopLossHabiltado"
                                class="mr-2"
                              >
                                Habilitar Stop Loss
                              </b-form-checkbox>
                            </div>
                            <validation-provider
                              #default="{ errors }"
                              name="Stop loss"
                              :rules="{ required: stopLossHabiltado }"
                            >
                              <b-input-group>
                                <b-input-group-prepend is-text>
                                  <feather-icon
                                    icon="TrendingDownIcon"
                                    size="16"
                                    class="text-danger"
                                  />
                                </b-input-group-prepend>
                                <b-form-input
                                  id="stoploss"
                                  v-model="sinal.stopLoss"
                                  :disabled="!stopLossHabiltado"
                                  :placeholder="stopLossHabiltado ? '0.00000000' : 'Sem stop loss'"
                                  type="number"
                                  step="any"
                                />
                              </b-input-group>
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </div>
                  </b-form>
                </validation-observer>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- RESUMO DA OPERAÇÃO -->
          <b-col
            cols="12"
            lg="6"
            xl="7"
            class="order-lg-2 order-2"
          >
            <b-card
              no-body
              class="card-statistics"
            >
              <b-card-header class="d-flex justify-content-between align-items-center">
                <h4 class="card-title mb-0">
                  <feather-icon
                    icon="BarChart2Icon"
                    class="mr-1"
                  />
                  Resumo da Operação
                </h4>
                <div class="header-pair-info d-flex align-items-center">
                  <div
                    v-if="selectedExchange && exchangeConfig[selectedExchange]"
                    class="exchange-badge mr-2"
                  >
                    <img
                      :src="require(`@/assets/images/logo/${selectedExchange}-logo.png`)"
                      :alt="exchangeConfig[selectedExchange].name"
                      class="exchange-logo-badge mr-1"
                    >
                    <small class="exchange-name-badge">{{ exchangeConfig[selectedExchange].name }}</small>
                  </div>
                  <b-badge
                    v-if="sinal.moedaTrade"
                    :variant="ticker && ticker.percent > 0 ? 'light-success' : 'light-danger'"
                    pill
                    class="pair-badge"
                  >
                    {{ paridade }}
                  </b-badge>
                </div>
              </b-card-header>
              <b-card-body class="summary-body">
                <!-- PAIR INFO HEADER -->
                <div class="pair-header">
                  <div class="d-flex align-items-center mb-3">
                    <b-avatar
                      v-if="sinal.moedaTrade"
                      :src="getIcon(sinal.moedaTrade)"
                      size="32"
                      class="mr-2"
                    />
                    <div
                      v-else
                      class="pair-placeholder mr-2"
                    >
                      <feather-icon
                        icon="DollarSignIcon"
                        size="16"
                      />
                    </div>
                    <div>
                      <h4 class="pair-name mb-0">
                        {{ paridade || 'Selecione um par' }}
                      </h4>
                      <div
                        v-if="isTickerLoaded"
                        class="price-info"
                      >
                        <span class="current-price">{{ paridadeEscolhida }} {{ ticker.price }}</span>
                        <span
                          class="price-change ml-2"
                          :class="ticker.percent > 0 ? 'text-success' : 'text-danger'"
                        >
                          {{ ticker.percent > 0 ? '+' : '' }}{{ ticker.percent }}%
                        </span>
                      </div>
                      <div
                        v-else-if="sinal.moedaTrade && paridade"
                        class="price-info"
                      >
                        <small class="text-muted">Carregando preços...</small>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- ORDER SUMMARY -->
                <div class="order-summary mb-4">
                  <h6 class="summary-title">
                    Resumo da Ordem
                  </h6>
                  <div class="summary-content">
                    <div class="summary-row">
                      <span class="summary-label">Par</span>
                      <span class="summary-value">
                        {{ paridade || '--' }}
                      </span>
                    </div>
                    <div class="summary-row">
                      <span class="summary-label">Tipo</span>
                      <span class="summary-value">
                        {{ sinal.ordemTipo ? tipoOrdemCompra : '--' }}
                      </span>
                    </div>
                    <div class="summary-row">
                      <span class="summary-label">Entrada</span>
                      <span class="summary-value">
                        {{ valorCompra ? `${paridadeEscolhida} ${valorCompra}` : '--' }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- PREÇO ATUAL E DIFERENÇA -->
                <div
                  v-if="valorCompra && isTickerLoaded"
                  class="current-price-summary mb-4"
                >
                  <h6 class="summary-title">
                    <feather-icon
                      icon="ActivityIcon"
                      size="16"
                      class="mr-1"
                    />
                    Preço Atual vs Entrada
                  </h6>
                  <div class="current-price-content">
                    <div class="price-comparison">
                      <div class="price-item">
                        <span class="price-label">Preço Atual:</span>
                        <span class="price-value current-price-value">
                          {{ paridadeEscolhida }} {{ ticker.price }}
                        </span>
                      </div>
                      <div class="price-item">
                        <span class="price-label">Preço Entrada:</span>
                        <span class="price-value entry-price-value">
                          {{ paridadeEscolhida }} {{ valorCompra }}
                        </span>
                      </div>
                      <div class="price-difference">
                        <span class="difference-label">Diferença:</span>
                        <span
                          class="difference-value"
                          :class="getPriceDifferenceClass()"
                        >
                          {{ getPriceDifferenceText() }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- TARGETS SUMMARY -->
                <div class="targets-summary">
                  <h6 class="summary-title">
                    Alvos & Proteção
                  </h6>

                  <div class="targets-list">
                    <!-- ALVO 1 -->
                    <div
                      v-if="sinal.alvo1"
                      class="target-row"
                    >
                      <div class="target-info">
                        <span class="target-name">Alvo 1</span>
                        <div class="target-price-container">
                          <span class="target-price-value">{{ sinal.alvo1 }}</span>
                          <span class="target-currency"> {{ paridadeEscolhida }}</span>
                        </div>
                      </div>
                      <div
                        v-if="valorCompra"
                        class="target-gain text-success"
                      >
                        +{{ getPercentDiff(valorCompra, sinal.alvo1) }}%
                      </div>
                    </div>

                    <!-- ALVO 2 -->
                    <div
                      v-if="sinal.alvo2"
                      class="target-row"
                    >
                      <div class="target-info">
                        <span class="target-name">Alvo 2</span>
                        <div class="target-price-container">
                          <span class="target-price-value">{{ sinal.alvo2 }}</span>
                          <span class="target-currency"> {{ paridadeEscolhida }}</span>
                        </div>
                      </div>
                      <div
                        v-if="valorCompra"
                        class="target-gain text-success"
                      >
                        +{{ getPercentDiff(valorCompra, sinal.alvo2) }}%
                      </div>
                    </div>

                    <!-- STOP LOSS -->
                    <div
                      v-if="sinal.stopLoss && sinal.stopLoss !== 'na'"
                      class="target-row"
                    >
                      <div class="target-info">
                        <span class="target-name">Stop Loss</span>
                        <div class="target-price-container">
                          <span class="target-price-value">{{ sinal.stopLoss }}</span>
                          <span class="target-currency"> {{ paridadeEscolhida }}</span>
                        </div>
                      </div>
                      <div
                        v-if="valorCompra"
                        class="target-loss text-danger"
                      >
                        {{ getPercentDiff(valorCompra, sinal.stopLoss) }}%
                      </div>
                    </div>

                    <!-- MENSAGEM QUANDO NÃO HÁ ALVOS -->
                    <div
                      v-if="!sinal.alvo1 && !sinal.alvo2 && (!sinal.stopLoss || sinal.stopLoss === 'na')"
                      class="no-targets-message"
                    >
                      <feather-icon
                        icon="InfoIcon"
                        size="16"
                        class="mr-2 text-muted"
                      />
                      <span class="text-muted">Configure os alvos e stop loss para ver os indicadores</span>
                    </div>
                  </div>
                </div>

                <!-- BOTÃO ENVIAR DENTRO DO RESUMO -->
                <div class="mt-4 d-flex justify-content-center">
                  <b-button
                    variant="primary"
                    size="lg"
                    type="button"
                    class="btn-send-signal"
                    @click="confirmarSalvar"
                  >
                    <feather-icon
                      icon="SendIcon"
                      class="mr-2"
                    />
                    Enviar Sinal
                  </b-button>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </b-overlay>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BFormGroup, BFormSelect, BFormInput, BButton, BAvatar, BSpinner, BOverlay, BInputGroup, BInputGroupPrepend, BFormCheckbox, BCardHeader, BCardBody, BBadge, BFormSelectOption,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import store from '@/store'
import utilsMixin from '@/mixins/utilsMixin'
import { subscribeSymbol, unSubscribeSymbol, getTicker } from '@/services/websocketUtils'
import { percentDiferenca } from '@/utils/filter'
import novaOperacaoModule from './novaOperacaoModule'

const NOVA_OPERACAO_AGENTE_SPOT_STORE_MODULE_NAME = 'nova-operacao-agente-spot'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormCheckbox,
    BButton,
    BAvatar,
    BSpinner,
    BOverlay,
    BCardHeader,
    BCardBody,
    BBadge,
    BFormSelectOption,
    ValidationProvider,
    ValidationObserver,
  },

  mixins: [utilsMixin],

  data() {
    return {
      loadingOverlay: false,
      loadingTicker: false,
      agentePerfis: [],
      debounceTimer: null,
      percentDiferenca,
      required,
      paridadeEscolhida: null,
      selectedExchange: null,
      previousSymbol: null,
      sinal: {
        moedaTrade: null,
        moedaParidade: null,
        ordemTipo: 'limit',
        agentePerfilTradeId: null,
        alvo1: null,
        alvo2: null,
        stopLoss: null,
        valorCompra: null,
      },

      stopLossHabiltado: false,
      alvoHabilitado: true,

      exchangeConfig: {
        binance: {
          id: 1,
          name: 'Binance',
          variant: 'light-warning',
          cardClass: 'binance-theme',
          color: '#F0B90B',
        },
        mexc: {
          id: 2,
          name: 'MEXC',
          variant: 'light-primary',
          cardClass: 'mexc-theme',
          color: '#005CE6',
        },
      },

      paridadeOptions: [
        { text: 'USDT', value: 'USDT' },
        { text: 'USDC', value: 'USDC' },
        { text: 'BTC', value: 'BTC' },
      ],

      tipoOrdensOptions: [
        { text: 'Stop Limite', value: 'stop_limit' },
        { text: 'Limite', value: 'limit' },
        { text: 'Mercado', value: 'market' },
      ],
    }
  },
  computed: {
    fixedExchanges() {
      return this.exchangeConfig
    },

    firstActiveExchange() {
      return 'binance'
    },

    // Filtrar perfis por produto SPOT e corretora selecionada
    perfisFiltrados() {
      if (!this.selectedExchange) return []

      return this.agentePerfis.filter(perfil => perfil.produto === 'SPOT'
        && perfil.corretora.toLowerCase() === this.selectedExchange)
    },

    // Verificar se deve mostrar o seletor de perfil
    deveMostrarPerfil() {
      return this.perfisFiltrados.length > 1
    },

    agentePerfil() {
      if (this.sinal.agentePerfilTradeId) {
        return this.agentePerfis.find(v => v.id === this.sinal.agentePerfilTradeId).nome
      }
      return null
    },

    tipoOrdemCompra() {
      if (this.sinal.ordemTipo) {
        return this.tipoOrdensOptions.find(v => v.value === this.sinal.ordemTipo).text
      }
      return null
    },

    paridade() {
      if (this.sinal.moedaTrade != null && this.paridadeEscolhida != null) {
        return `${this.sinal.moedaTrade}${this.paridadeEscolhida}`
      }
      return ''
    },

    ticker() {
      if (this.paridade !== '') {
        const coin = this.paridade
        return getTicker(coin, this.selectedExchange, this.$store)
      }
      return ''
    },

    // Verificar se o ticker está carregado com dados válidos
    isTickerLoaded() {
      return this.ticker && this.ticker.price && parseFloat(this.ticker.price) > 0
    },

    valorCompra() {
      if (this.sinal.ordemTipo === 'limit' || this.sinal.ordemTipo === 'stop_limit') {
        return this.sinal.valorCompra
      }
      return this.ticker.price
    },
  },

  watch: {
    paridade: {
      handler(newParidade, oldParidade) {
        // Limpar timer anterior se existir
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer)
        }

        // Aguardar 500ms antes de executar as ações
        this.debounceTimer = setTimeout(() => {
          if (oldParidade && oldParidade !== newParidade) {
            // Desinscrever do símbolo anterior
            unSubscribeSymbol(oldParidade, this.selectedExchange)
          }

          if (newParidade && this.selectedExchange) {
            // Inscrever no novo símbolo
            subscribeSymbol(newParidade, this.selectedExchange)
          }
        }, 500) // 500ms de delay
      },
      immediate: false,
    },

    selectedExchange: {
      handler(newExchange, oldExchange) {
        // Limpar timer anterior se existir
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer)
        }

        // Aguardar 300ms antes de executar as ações
        this.debounceTimer = setTimeout(() => {
          // Se há uma paridade selecionada, fazer a busca na nova corretora
          if (this.paridade && oldExchange && newExchange !== oldExchange) {
            // Desinscrever da corretora anterior
            unSubscribeSymbol(this.paridade, oldExchange)

            // Limpar dados do ticker anterior para forçar nova busca
            this.clearTickerData(oldExchange)

            // Aguardar um pequeno delay para garantir que a conexão anterior foi fechada
            setTimeout(() => {
              // Inscrever na nova corretora (força nova conexão)
              subscribeSymbol(this.paridade, newExchange)
            }, 100)
          }
        }, 300) // 300ms de delay
      },
      immediate: false,
    },
  },

  created() {
    if (!store.hasModule(NOVA_OPERACAO_AGENTE_SPOT_STORE_MODULE_NAME)) store.registerModule(NOVA_OPERACAO_AGENTE_SPOT_STORE_MODULE_NAME, novaOperacaoModule)
    this.getAgentePerfis()

    // Definir a primeira corretora como selecionada (com persistência)
    try {
      const persisted = JSON.parse(localStorage.getItem('userData') || '{}').selectedExchange
      this.selectedExchange = this.exchangeConfig[persisted] ? persisted : this.firstActiveExchange
    } catch (e) {
      this.selectedExchange = this.firstActiveExchange
    }
  },

  beforeDestroy() {
    // Limpar timer de debounce
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    if (store.hasModule(NOVA_OPERACAO_AGENTE_SPOT_STORE_MODULE_NAME)) store.unregisterModule(NOVA_OPERACAO_AGENTE_SPOT_STORE_MODULE_NAME)
  },

  mounted() {
    this.paridadeEscolhida = this.paridadeOptions[0].value
  },

  methods: {
    getAgentePerfis() {
      this.$store.dispatch('nova-operacao-agente-spot/getAgentePerfis')
        .then(response => {
          // Filtrar apenas perfis SPOT
          this.agentePerfis = response.data.filter(a => a.produto === 'SPOT')

          // Selecionar automaticamente se há apenas 1 perfil para a corretora atual
          this.selecionarPerfilAutomatico()
        })
    },

    // Função para calcular diferença percentual (usada no template)
    getPercentDiff(valor1, valor2) {
      if (!valor1 || !valor2) return 0
      const diff = ((parseFloat(valor2) - parseFloat(valor1)) / parseFloat(valor1)) * 100
      return diff.toFixed(2)
    },

    // Função para calcular a diferença entre preço atual e preço de entrada
    getPriceDifference() {
      if (!this.valorCompra || !this.ticker || !this.ticker.price) return 0
      const currentPrice = parseFloat(this.ticker.price)
      const entryPrice = parseFloat(this.valorCompra)
      return ((currentPrice - entryPrice) / entryPrice) * 100
    },

    // Função para obter o texto da diferença de preço
    getPriceDifferenceText() {
      const diff = this.getPriceDifference()
      if (diff === 0) return '0.00%'
      const sign = diff > 0 ? '+' : ''
      return `${sign}${diff.toFixed(2)}%`
    },

    // Função para obter a classe CSS baseada na diferença de preço
    getPriceDifferenceClass() {
      const diff = this.getPriceDifference()
      if (diff > 0) return 'text-success'
      if (diff < 0) return 'text-danger'
      return 'text-muted'
    },

    confirmarSalvar() {
      this.$swal({
        title: 'Confirmar as alterações',
        text: 'Revise os valores. Ao clicar no botão verde, você vai confirmar o envio do seu sinal e emitir uma ordem de compra.',
        icon: 'warning',
        confirmButtonText: 'Ok, está tudo certo!',
        showCancelButton: true,
        cancelButtonText: 'Abortar',
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.submitForm()
        }
      })
    },

    clearForm() {
      // Resetar todos os campos do sinal
      this.sinal.moedaTrade = null
      this.sinal.moedaParidade = null
      this.sinal.ordemTipo = 'limit'
      this.sinal.alvo1 = null
      this.sinal.alvo2 = null
      this.sinal.stopLoss = null
      this.sinal.valorCompra = null
      this.sinal.valorCompraStop = null

      // Resetar estados dos checkboxes
      this.stopLossHabiltado = false
      this.alvoHabilitado = true

      // Resetar paridade escolhida para o padrão
      this.paridadeEscolhida = this.paridadeOptions[0].value

      // Resetar o formulário de validação
      this.$nextTick(() => {
        if (this.$refs.formSignal) {
          this.$refs.formSignal.reset()
        }
      })

      // Limpar timer de debounce se existir
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
        this.debounceTimer = null
      }

      // Limpar dados do ticker se houver paridade anterior
      if (this.paridade && this.selectedExchange) {
        unSubscribeSymbol(this.paridade, this.selectedExchange)
        this.clearTickerData(this.selectedExchange)
      }
    },

    submitForm() {
      this.$refs.formSignal.validate().then(success => {
        if (success) {
          this.loadingOverlay = true
          this.sinal.moedaParidade = this.sinal.moedaTrade + this.paridadeEscolhida

          if (this.stopLossHabiltado === false) {
            this.sinal.stopLoss = 'na'
          }

          if (this.alvoHabilitado === false) {
            this.sinal.alvo1 = 'na'
          }

          this.$store.dispatch('nova-operacao-agente-spot/enviarSinal', this.sinal)
            .then(() => {
              this.$swal({
                icon: 'success',
                title: 'Sucesso',
                html: 'Sinal enviado com sucesso',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
                buttonsStyling: false,
              })

              // Aguardar um pouco antes de limpar o formulário para garantir que o SweetAlert seja exibido
              setTimeout(() => {
                this.clearForm()
              }, 100)
            })
            .catch(error => {
              this.$swal({
                title: 'Error!',
                text: error.data.message,
                icon: 'error',
                customClass: {
                  confirmButton: 'btn btn-primary',
                },
                buttonsStyling: false,
              })
            })
            .finally(() => {
              this.loadingOverlay = false
            })
        }
      })
    },

    selectExchange(exchange) {
      // Verificar se a corretora está disponível nas fixas
      if (!this.exchangeConfig[exchange]) {
        return
      }

      const oldExchange = this.selectedExchange
      this.selectedExchange = exchange

      // Salvar no localStorage
      try {
        const currentUserData = JSON.parse(localStorage.getItem('userData') || '{}')
        currentUserData.selectedExchange = exchange
        localStorage.setItem('userData', JSON.stringify(currentUserData))
      } catch (e) { /* ignore */ }

      // Selecionar perfil automaticamente para a nova corretora
      this.selecionarPerfilAutomatico()

      // Se há uma paridade selecionada, fazer a busca imediata na nova corretora
      if (this.paridade && oldExchange && oldExchange !== exchange) {
        // Limpar timer anterior se existir
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer)
        }

        // Aguardar 200ms antes de executar as ações
        this.debounceTimer = setTimeout(() => {
          // Desinscrever da corretora anterior
          unSubscribeSymbol(this.paridade, oldExchange)

          // Limpar dados do ticker anterior para forçar nova busca
          this.clearTickerData(oldExchange)

          // Aguardar um pequeno delay para garantir que a conexão anterior foi fechada
          setTimeout(() => {
            // Inscrever na nova corretora (força nova conexão)
            subscribeSymbol(this.paridade, exchange)
          }, 100)
        }, 200) // 200ms de delay
      }
    },

    // Selecionar perfil automaticamente baseado na corretora
    selecionarPerfilAutomatico() {
      if (this.selectedExchange) {
        const perfisCorretora = this.perfisFiltrados

        if (perfisCorretora.length === 1) {
          // Se há apenas 1 perfil, selecionar automaticamente
          this.sinal.agentePerfilTradeId = perfisCorretora[0].id
        } else if (perfisCorretora.length > 1) {
          // Se há múltiplos perfis, limpar seleção para o usuário escolher
          this.sinal.agentePerfilTradeId = null
        } else {
          // Se não há perfis para esta corretora, limpar seleção
          this.sinal.agentePerfilTradeId = null
        }
      }
    },

    // Limpar dados do ticker de uma corretora específica
    clearTickerData(exchange) {
      if (exchange === 'binance' && this.$store.state.binanceSpot.tickers[this.paridade]) {
        // Limpar dados do ticker da Binance
        this.$store.commit('binanceSpot/CLEAR_TICKER', this.paridade)
      } else if (exchange === 'mexc' && this.$store.state.mexcSpot.tickers[this.paridade]) {
        // Limpar dados do ticker da MEXC
        this.$store.commit('mexcSpot/CLEAR_TICKER', this.paridade)
      }
    },

    // Lighten a HEX color by percentage (mix with white)
    lightenColor(hexColor, percent) {
      try {
        if (!hexColor) return '#ffffff'
        let hex = hexColor.toString().replace('#', '')
        if (hex.length === 3) {
          hex = hex.split('').map(ch => ch + ch).join('')
        }
        const r = parseInt(hex.substring(0, 2), 16)
        const g = parseInt(hex.substring(2, 4), 16)
        const b = parseInt(hex.substring(4, 6), 16)
        const p = Math.max(0, Math.min(100, Number(percent))) / 100
        const rn = Math.round(r + (255 - r) * p)
        const gn = Math.round(g + (255 - g) * p)
        const bn = Math.round(b + (255 - b) * p)
        const toHex = v => v.toString(16).padStart(2, '0')
        return `#${toHex(rn)}${toHex(gn)}${toHex(bn)}`
      } catch (e) {
        return hexColor
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';
</style>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

$theme-dark-card-bg: #0F0F11;
$theme-dark-border-color: rgba(255, 255, 255, 0.10);
$theme-dark-body-bg: #000000;
$theme-dark-headings-color: #ffffff;
$theme-dark-body-color: #c9ccd1;

.content-header {
  .breadcrumb-wrapper {
    .breadcrumb {
      background: transparent;
      margin-bottom: 0;

      .breadcrumb-item {
        + .breadcrumb-item::before {
          content: '>';
        }

        &.active {
          color: $text-muted;
        }

        a {
          color: $primary;
          text-decoration: none;

          &:hover {
            color: $primary;
          }
        }
      }
    }
  }
}

.content-body {
  .card-transaction,
  .card-statistics {
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    border: none;
    border-radius: 0.428rem;

    .dark-layout & {
      box-shadow: $theme-dark-box-shadow;
      background: $theme-dark-card-bg;
    }

    .card-header {
      border-bottom: 1px solid rgba(34, 41, 47, 0.125);
      padding: 1.5rem;

      .dark-layout & {
        border-bottom-color: $theme-dark-border-color;
        background: $theme-dark-card-bg;
      }

      .card-title {
        color: $headings-color;
        font-size: 1.125rem;
        font-weight: 600;

        .dark-layout & {
          color: $theme-dark-headings-color;
        }
      }

      .header-pair-info {
        .exchange-badge {
          display: flex;
          align-items: center;
          background: rgba($primary, 0.1);
          border: 1px solid rgba($primary, 0.2);
          border-radius: 1rem;
          padding: 0.25rem 0.75rem;
          transition: all 0.2s ease;

          &:hover {
            background: rgba($primary, 0.15);
            border-color: rgba($primary, 0.3);
          }

          .dark-layout & {
            background: rgba($primary, 0.15);
            border-color: rgba($primary, 0.3);

            &:hover {
              background: rgba($primary, 0.2);
              border-color: rgba($primary, 0.4);
            }
          }

          .exchange-logo-badge {
            width: 16px;
            height: 16px;
            object-fit: contain;
            flex-shrink: 0;
          }

          .exchange-name-badge {
            font-size: 0.75rem;
            font-weight: 600;
            color: $primary;
            text-transform: uppercase;
            letter-spacing: 0.5px;

            .dark-layout & {
              color: lighten($primary, 10%);
            }
          }
        }

        .pair-badge {
          font-size: 0.875rem;
          font-weight: 600;
          padding: 0.5rem 1rem;
        }
      }
    }

    .card-body {
      padding: 1.5rem;

      .dark-layout & {
        background: $theme-dark-card-bg;
      }
    }
  }
}

.form-section {
  background: $white;
  border: 1px solid $border-color;
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
  transition: all 0.2s ease;

  .dark-layout & {
    background: $theme-dark-card-bg;
    border-color: $theme-dark-border-color;
    box-shadow: $theme-dark-box-shadow;
  }

  &:hover {
    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.05);

    .dark-layout & {
      box-shadow: 0 2px 15px rgba(0, 0, 0, 0.3);
    }
  }

  .section-title {
    color: $headings-color;
    font-weight: 600;
    font-size: 0.95rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid $primary;
    position: relative;

    .dark-layout & {
      color: $theme-dark-headings-color;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 30px;
      height: 2px;
      background: $primary;
    }
  }

  .form-group {
    margin-bottom: 1rem;

    label {
      font-weight: 600;
      color: $headings-color;
      margin-bottom: 0.5rem;
      font-size: 0.875rem;

      .dark-layout & {
        color: $theme-dark-label-color;
      }
    }

    .form-control,
    .form-select {
      border: 2px solid $border-color;
      border-radius: 0.5rem;
      padding: 0.75rem 1rem;
      font-size: 0.875rem;
      transition: all 0.2s ease;

      .dark-layout & {
        background: $theme-dark-input-bg;
        border-color: $theme-dark-input-border-color;
        color: $theme-dark-body-color;

        &::placeholder {
          color: $theme-dark-input-placeholder-color;
        }

        &:focus {
          border-color: $primary;
          box-shadow: 0 0 0 0.2rem rgba($primary, 0.15);
          background: $theme-dark-input-bg;
        }

        &:disabled {
          background: $theme-dark-input-disabled-bg;
          border-color: $theme-dark-input-disabled-border-color;
          color: $theme-dark-text-muted-color;
        }
      }

      &:focus {
        border-color: $primary;
        box-shadow: 0 0 0 0.2rem rgba($primary, 0.15);
      }
    }

    .input-group {
      .input-group-prepend {
        .input-group-text {
          background: $gray-100;
          border: 2px solid $border-color;
          border-right: none;
          color: $text-muted;

          .dark-layout & {
            background: $theme-dark-input-bg;
            border-color: $theme-dark-input-border-color;
            color: $theme-dark-text-muted-color;
          }
        }
      }

      .form-control {
        border-left: none;

        &:focus {
          + .input-group-prepend .input-group-text {
            border-color: $primary;
          }
        }
      }
    }
  }
}

// Submit Button
.btn-block {
  background: $primary;
  border: none;
  border-radius: 0.5rem;
  font-weight: 700;
  padding: 0.75rem 1.5rem;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px rgba($primary, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba($primary, 0.4);
  }

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba($primary, 0.25);
  }
}

// Modern Trading Layout
.summary-body {
  padding: 1.5rem !important;

  .dark-layout & {
    background: $theme-dark-card-bg;
  }
}

.pair-header {
  padding-bottom: 1rem;
  border-bottom: 1px solid $border-color;
  margin-bottom: 1.5rem;

  .dark-layout & {
    border-bottom-color: $theme-dark-border-color;
  }

  .pair-placeholder {
    width: 32px;
    height: 32px;
    background: $gray-100;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $text-muted;

    .dark-layout & {
      background: $theme-dark-body-bg;
      color: $theme-dark-text-muted-color;
    }
  }

  .pair-name {
    font-size: 1.25rem;
    font-weight: 700;
    color: $headings-color;

    .dark-layout & {
      color: $theme-dark-headings-color;
    }
  }

  .price-info {
    .current-price {
      font-size: 1rem;
      font-weight: 600;
      color: $text-muted;

      .dark-layout & {
        color: $theme-dark-text-muted-color;
      }
    }

    .price-change {
      font-size: 0.875rem;
      font-weight: 600;
    }
  }
}

.market-data {
  .market-source-info {
    text-align: center;

    small {
      font-size: 0.7rem;
      font-weight: 500;
      justify-content: center;
    }
  }

  .data-item {
    text-align: center;
    padding: 1rem;
    background: $gray-50;
    border-radius: 0.5rem;
    border: 1px solid $border-color;

    .dark-layout & {
      background: $theme-dark-body-bg;
      border-color: $theme-dark-border-color;
    }

    .data-label {
      font-size: 0.75rem;
      color: $text-muted;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 0.5rem;
      font-weight: 600;
    }

    .data-value {
      font-size: 1rem;
      font-weight: 700;
      color: $headings-color;

      .dark-layout & {
        color: $theme-dark-headings-color;
      }
    }
  }
}

.order-summary,
.targets-summary {
  .summary-title {
    font-size: 0.875rem;
    font-weight: 700;
    color: $headings-color;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid $primary;

    .dark-layout & {
      color: $theme-dark-headings-color;
    }
  }

  .summary-content {
    background: $gray-50;
    border-radius: 0.5rem;
    padding: 1rem;
    border: 1px solid $border-color;

    .dark-layout & {
      background: $theme-dark-body-bg;
      border-color: $theme-dark-border-color;
    }
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;

    &:not(:last-child) {
      border-bottom: 1px solid $border-color;

      .dark-layout & {
        border-bottom-color: $theme-dark-border-color;
      }
    }

    .summary-label {
      font-size: 0.875rem;
      color: $text-muted;
      font-weight: 600;
    }

    .summary-value {
      font-size: 0.875rem;
      font-weight: 700;
      color: $headings-color;

      .dark-layout & {
        color: $theme-dark-headings-color;
      }
    }

    .summary-exchange-logo {
      width: 16px;
      height: 16px;
      object-fit: contain;
      flex-shrink: 0;
    }
  }
}

.targets-list {
  .target-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    background: $white;
    border: 1px solid $border-color;
    border-radius: 0.5rem;
    margin-bottom: 0.75rem;
    transition: all 0.2s ease;

    .dark-layout & {
      background: $theme-dark-card-bg;
      border-color: $theme-dark-border-color;
    }

    &:hover {
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

      .dark-layout & {
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
      }
    }

    .target-info {
      display: flex;
      flex-direction: column;

      .target-name {
        font-size: 0.875rem;
        color: $headings-color;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-weight: 700;
        margin-bottom: 0.375rem;
        text-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);

        .dark-layout & {
          color: $theme-dark-headings-color;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        }
      }

      .target-price-container {
        display: flex;
        align-items: baseline;
        gap: 0.25rem;

        .target-price-value {
          font-size: 1.25rem;
          font-weight: 900;
          color: $primary;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

          .dark-layout & {
            color: lighten($primary, 15%);
            text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
          }
        }

        .target-currency {
          font-size: 0.875rem;
          color: $text-muted;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          opacity: 0.8;

          .dark-layout & {
            color: $theme-dark-text-muted-color;
            opacity: 0.9;
          }
        }
      }
    }

    .target-gain,
    .target-loss {
      font-size: 1.125rem;
      font-weight: 900;
      padding: 0.5rem 1rem;
      border-radius: 0.5rem;
      background: rgba($success, 0.15);
      border: 2px solid rgba($success, 0.3);
      color: $success;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      box-shadow: 0 2px 4px rgba($success, 0.2);

      .dark-layout & {
        background: rgba($success, 0.2);
        border-color: rgba($success, 0.4);
        box-shadow: 0 2px 6px rgba($success, 0.3);
      }

      &.text-danger {
        background: rgba($danger, 0.15);
        border: 2px solid rgba($danger, 0.3);
        color: $danger;
        box-shadow: 0 2px 4px rgba($danger, 0.2);

        .dark-layout & {
          background: rgba($danger, 0.2);
          border-color: rgba($danger, 0.4);
          box-shadow: 0 2px 6px rgba($danger, 0.3);
        }
      }
    }
  }

  .no-targets-message {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem;
    background: $gray-50;
    border: 1px dashed $border-color;
    border-radius: 0.5rem;
    color: $text-muted;
    font-size: 0.875rem;
    font-style: italic;

    .dark-layout & {
      background: rgba($theme-dark-body-bg, 0.5);
      border-color: $theme-dark-border-color;
      color: $theme-dark-text-muted-color;
    }
  }
}

// Seção de Preço Atual vs Entrada
.current-price-summary {
  .current-price-content {
    background: $gray-50;
    border-radius: 0.5rem;
    padding: 1rem;
    border: 1px solid $border-color;

    .dark-layout & {
      background: $theme-dark-body-bg;
      border-color: $theme-dark-border-color;
    }

    .price-comparison {
      .price-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 0;

        &:not(:last-child) {
          border-bottom: 1px solid rgba($border-color, 0.3);

          .dark-layout & {
            border-bottom-color: rgba($theme-dark-border-color, 0.3);
          }
        }

        .price-label {
          font-size: 0.875rem;
          color: $text-muted;
          font-weight: 600;

          .dark-layout & {
            color: $theme-dark-text-muted-color;
          }
        }

        .price-value {
          font-size: 1rem;
          font-weight: 700;
          color: $headings-color;

          .dark-layout & {
            color: $theme-dark-headings-color;
          }

          &.current-price-value {
            color: $primary;
            font-weight: 800;
          }

          &.entry-price-value {
            color: $info;
            font-weight: 800;
          }
        }
      }

      .price-difference {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.75rem 0;
        margin-top: 0.5rem;
        background: rgba($primary, 0.05);
        border-radius: 0.375rem;
        padding: 0.75rem 1rem;

        .dark-layout & {
          background: rgba($primary, 0.1);
        }

        .difference-label {
          font-size: 0.875rem;
          color: $text-muted;
          font-weight: 600;

          .dark-layout & {
            color: $theme-dark-text-muted-color;
          }
        }

        .difference-value {
          font-size: 1.125rem;
          font-weight: 800;
          padding: 0.25rem 0.75rem;
          border-radius: 0.375rem;
          background: $white;
          border: 1px solid $border-color;

          .dark-layout & {
            background: $theme-dark-card-bg;
            border-color: $theme-dark-border-color;
          }

          &.text-success {
            background: rgba($success, 0.1);
            border-color: rgba($success, 0.2);
            color: $success;
          }

          &.text-danger {
            background: rgba($danger, 0.1);
            border-color: rgba($danger, 0.2);
            color: $danger;
          }

          &.text-muted {
            background: rgba($text-muted, 0.1);
            border-color: rgba($text-muted, 0.2);
            color: $text-muted;
          }
        }
      }
    }
  }
}

// Form Controls
.form-control-lg {
  font-size: 1rem;
  padding: 0.75rem 1rem;
}

.btn-block {
  font-weight: 600;
  padding: 0.875rem 1.5rem;
  font-size: 1rem;
}

// Botão Enviar Sinal Personalizado
.btn-send-signal {
  font-weight: 700;
  padding: 1rem 3rem;
  font-size: 1.125rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 20px rgba($primary, 0.3);
  transition: all 0.3s ease;
  min-width: 280px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba($primary, 0.4);
  }

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba($primary, 0.25);
  }

  // Responsivo para mobile
  @media (max-width: 991.98px) {
    width: 100%;
    min-width: auto;
  }
}

/* Responsive optimizations */
@media (max-width: 768px) {
  .form-section {
    padding: 0.75rem 1rem;
    margin-bottom: 0.75rem;
  }

  .section-title {
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
    padding-bottom: 0.375rem;
  }

  .form-group {
    margin-bottom: 0.75rem;
  }
}

/* Exchange Selector - Form Style */
.exchange-selector-form {
  .exchange-options {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
  }

  .exchange-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem;
    background: $white;
    border: 2px solid $border-color;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;

    .dark-layout & {
      background: $theme-dark-card-bg;
      border-color: $theme-dark-border-color;
    }

    &:hover {
      border-color: rgba($primary, 0.5);
      box-shadow: 0 2px 8px rgba($primary, 0.15);

      .dark-layout & {
        box-shadow: 0 2px 8px rgba($primary, 0.25);
      }
    }

    &.active {
      border-color: $primary;
      background: rgba($primary, 0.05);
      box-shadow: 0 2px 12px rgba($primary, 0.2);

      .dark-layout & {
        background: rgba($primary, 0.1);
        box-shadow: 0 2px 12px rgba($primary, 0.3);
      }
    }

    .option-content {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .exchange-logo {
      width: 28px;
      height: 28px;
      object-fit: contain;
      flex-shrink: 0;

      @media (max-width: 768px) {
        width: 24px;
        height: 24px;
      }
    }

    .exchange-info {
      display: flex;
      flex-direction: column;

      .exchange-name {
        font-weight: 600;
        font-size: 0.9rem;
        color: $headings-color;

        .dark-layout & {
          color: $theme-dark-headings-color;
        }
      }
    }

    .selected-indicator {
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 1;
      transition: opacity 0.2s ease;

      .feather {
        filter: drop-shadow(0 1px 2px rgba($success, 0.3));
      }
    }
  }
}

// Dark Layout Support - Additional overrides
.dark-layout {
  // Cards
  .card-transaction,
  .card-statistics {
    background: $theme-dark-card-bg;
    border-color: $theme-dark-border-color;
  }

  // Form sections
  .form-section {
    background: $theme-dark-card-bg;
    border-color: $theme-dark-border-color;
  }

  // Input groups
  .input-group-text {
    background: $theme-dark-input-bg;
    border-color: $theme-dark-input-border-color;
    color: $theme-dark-text-muted-color;
  }

  // Checkbox styling
  .custom-checkbox .custom-control-label::before {
    background-color: $theme-dark-input-bg;
    border-color: $theme-dark-input-border-color;
  }

  .custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
    background-color: $primary;
    border-color: $primary;
  }

  // Select dropdowns
  .form-select {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");

    .dark-layout & {
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23676d7d' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
    }
  }

  // Badge styling
  .badge {
    &.badge-light-primary {
      background-color: rgba($primary, 0.15);
      color: lighten($primary, 10%);
    }

    &.badge-light-success {
      background-color: rgba($success, 0.15);
      color: lighten($success, 10%);
    }

    &.badge-light-danger {
      background-color: rgba($danger, 0.15);
      color: lighten($danger, 10%);
    }
  }

  // Text colors
  .text-muted {
    color: $theme-dark-text-muted-color !important;
  }

  .text-white-50 {
    color: rgba(255, 255, 255, 0.5) !important;
  }
}

</style>
